import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  schema?: object;
  additionalSchema?: object | object[];
}

const SEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = '/og-image.jpg',
  ogType = 'website',
  schema,
  additionalSchema,
}: SEOProps) => {
  const siteUrl = 'https://ardsdothan.com';
  const siteName = "Ard's Plumbing & Electric";
  const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const fullImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

  const defaultSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': siteName,
    'image': `${siteUrl}/logo.jpg`,
    'telephone': '+1-334-692-5537',
    'email': 'support@ardsdothan.com',
    'url': siteUrl,
    'priceRange': '$$',
    'sameAs': [
      'https://share.google/XJlfsd8iISrcMfl82',
      'https://www.facebook.com/profile.php?id=61554768505242',
      'https://www.yelp.com/biz/ard-s-plumbing-and-electric-newton',
    ],
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': '733 West State Hwy 92',
      'addressLocality': 'Newton',
      'addressRegion': 'AL',
      'postalCode': '36352',
      'addressCountry': 'US',
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': 31.3409,
      'longitude': -85.6004,
    },
    'openingHoursSpecification': [
      {
        '@type': 'OpeningHoursSpecification',
        'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        'opens': '08:00',
        'closes': '17:00',
      },
    ],
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': '4.5',
      'bestRating': '5',
      'worstRating': '1',
      'ratingCount': '40',
    },
    'areaServed': [
      'Dothan', 'Enterprise', 'Ozark', 'Geneva', 'Headland',
      'Midland City', 'Daleville', 'Newton', 'Rehobeth',
    ].map(city => ({
      '@type': 'City',
      'name': city,
    })),
  };

  const finalSchema = schema || defaultSchema;

  // Support single object or array; arrays are wrapped in @graph
  const additionalSchemaBlock = additionalSchema
    ? Array.isArray(additionalSchema)
      ? { '@context': 'https://schema.org', '@graph': additionalSchema }
      : additionalSchema
    : null;

  return (
    <Helmet>
      {/* 1. Page-Specific Basic Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonical} />

      {/* 2. Dynamic Open Graph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      <meta property="fb:app_id" content="899161936198751" />

      {/* 3. Dynamic Twitter Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* 4. Structured Data for Google Search results */}
      <script type="application/ld+json">
        {JSON.stringify(finalSchema)}
      </script>
      {additionalSchemaBlock && (
        <script type="application/ld+json">
          {JSON.stringify(additionalSchemaBlock)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
